import { Line, LineSegments, Mesh, Sprite } from 'three';
import type { Object3D } from 'three';
import { ImportedModelMeshIdentityResolver } from '../ImportedModelMeshIdentityResolver';
import { ImportedModelSelectionObjectResolver } from '../ImportedModelSelectionObjectResolver';
import { isImportedTopologyProxy } from '../imported-topology-proxy-metadata';
import { isImportedModelTopologyHost } from '../../topology/ImportedModelTopologyRepository';

/**
 * imported 第一阶段拾取的资格策略；索引与无缓存入口复用相同规则。
 *
 * 显隐、metadata、拓扑绑定及祖先跳过标记均读取当前值，不缓存可能变化的判定结果。
 */
export class ImportedModelRaycastCandidatePolicy {
  /** @param isInstancedMesh 判断对象是否为当前正式 Unit，由运行时注册表提供。 */
  public constructor(private readonly isInstancedMesh: (object: Object3D) => boolean) {}

  /** @param object 物理场景对象。 @returns 类型上可能进入 imported 候选时为 true。 */
  public static isPotentialCandidate(object: Object3D): boolean {
    return object instanceof Mesh || object instanceof Sprite
      || (object instanceof Line && !(object instanceof LineSegments));
  }

  /** @param object 子树根。 @returns 原 imported 选择规则要求跳过整棵子树时为 true。 */
  public static shouldSkipSubtree(object: Object3D): boolean {
    return object.userData?.skipSelectionSubtree === true
      || isImportedModelTopologyHost(object)
      || isImportedTopologyProxy(object);
  }

  /**
   * 解析只随 metadata、Body 仓库或 Unit 注册变化的稳定语义分类。
   *
   * @param object 单个物理候选。
   * @returns 实例、普通 imported 或不合格三态分类。
   */
  public resolveSemanticKind(object: Object3D): ImportedModelRaycastCandidateSemanticKind {
    if (this.isInstancedMesh(object)) return 'instanced';
    if (!ImportedModelRaycastCandidatePolicy.isPotentialCandidate(object)) return 'ineligible';
    if (!ImportedModelSelectionObjectResolver.isImportedModelObject(object)) return 'ineligible';
    if (object instanceof Mesh && !ImportedModelMeshIdentityResolver.isImportedVisibleMesh(object)) {
      return 'ineligible';
    }
    return 'ordinary';
  }

  /**
   * 在已解析稳定语义上应用每次查询都必须读取的动态资格。
   *
   * @param object 单个物理候选。
   * @param semanticKind 当前权威版本下的稳定语义分类。
   * @returns 当前显隐、父链和临时标记仍允许参与拾取时为 true。
   */
  public acceptsWithSemanticKind(
    object: Object3D,
    semanticKind: ImportedModelRaycastCandidateSemanticKind,
  ): boolean {
    if (semanticKind === 'ineligible' || !object.visible) return false;
    let current: Object3D | null = object;
    while (current) {
      if (ImportedModelRaycastCandidatePolicy.shouldSkipSubtree(current)) return false;
      current = current.parent;
    }
    if (semanticKind === 'instanced') return true;
    if (object.userData?.skipSelection || object.userData?.isSelectionHighlight
      || object.userData?.isImportedModelTopologyProxy) return false;
    return true;
  }

  /** @param object 单个候选对象。 @returns 满足当前 imported 资格时为 true。 */
  public accepts(object: Object3D): boolean {
    return this.acceptsWithSemanticKind(object, this.resolveSemanticKind(object));
  }
}
